import { useEffect, useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Container,
  Row,
  Col,
  Table,
  Label,
  Input,
  Button,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
import axios from "axios";
import Header from "components/Headers/Header";
import Loader from "components/Loader";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const API_URL = process.env.REACT_APP_API_URL;
const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8", "#82ca9d", "#ffc658"];

const AdManagerKeyValueReport = () => {
  const [data, setData] = useState([]);
  const [filterType, setFilterType] = useState("utm_campaign");
  const [loading, setLoading] = useState(true);
  const [sortColumn, setSortColumn] = useState("revenue");
  const [sortDirection, setSortDirection] = useState("desc");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  // 🚀 Buscar dados
  const fetchKeyValueReport = async (custom = false) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("⚠️ Nenhum token encontrado!");
        setLoading(false);
        return;
      }

      const body = custom && startDate && endDate
        ? { filterType, startDate, endDate }
        : { filterType, relative: "YESTERDAY" };

      const res = await axios.post(`${API_URL}/admanager/keyvalue`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setData(res.data.rows || []);
      setCurrentPage(1);
    } catch (err) {
      console.error("❌ Erro ao buscar relatório Key-Value:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKeyValueReport(false);
  }, [filterType]);

  // 🔄 Ordenação
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  const sortedData = [...data].sort((a, b) => {
    const valA = Number(a[sortColumn]) || 0;
    const valB = Number(b[sortColumn]) || 0;
    return sortDirection === "asc" ? valA - valB : valB - valA;
  });

  // 🔢 Paginação
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const paginatedData = sortedData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // 📊 Gráfico de barras (receita por valor)
  const chartData = Object.values(
    data.reduce((acc, item) => {
      const keyVal = item.value || "(não definido)";
      if (!acc[keyVal]) acc[keyVal] = { name: keyVal, revenue: 0 };
      acc[keyVal].revenue += Number(item.revenue || 0);
      return acc;
    }, {})
  ).sort((a, b) => b.revenue - a.revenue);

  // 🥧 Gráfico de pizza (proporção de impressões por valor)
  const pieData = Object.values(
    data.reduce((acc, item) => {
      const keyVal = item.value || "(não definido)";
      if (!acc[keyVal]) acc[keyVal] = { name: keyVal, impressions: 0 };
      acc[keyVal].impressions += Number(item.impressions || 0);
      return acc;
    }, {})
  );

  // 📅 Formata datas AAAAMMDD → DD/MM/YYYY
  const formatDate = (val) => {
    if (!val) return "-";
    const str = val.toString();
    if (/^\d{8}$/.test(str)) {
      const y = str.slice(0, 4);
      const m = str.slice(4, 6);
      const d = str.slice(6, 8);
      return `${d}/${m}/${y}`;
    }
    return val;
  };

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        {/* 🔍 Filtros */}
        <Row className="mb-4">
          <Col md="3">
            <Label className="font-weight-bold text-dark">Início:</Label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </Col>
          <Col md="3">
            <Label className="font-weight-bold text-dark">Fim:</Label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </Col>
          <Col md="3">
            <Label className="font-weight-bold text-dark">Filtro UTM:</Label>
            <Input type="select" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
              <option value="utm_campaign">utm_campaign</option>
              <option value="utm_medium">utm_medium</option>
              <option value="utm_source">utm_source</option>
            </Input>
          </Col>
          <Col md="3" className="d-flex align-items-end justify-content-start">
            <Button color="primary" onClick={() => fetchKeyValueReport(true)}>
              🔍 Consultar
            </Button>
          </Col>
        </Row>

        {/* 📊 Gráficos */}
        <Row>
          <Col xl="6">
            <Card className="shadow" style={{ minHeight: 320 }}>
              <CardHeader>
                <h3 className="mb-0">💰 Receita por {filterType}</h3>
              </CardHeader>
              <CardBody
                className="d-flex justify-content-center align-items-center"
                style={{ height: 250 }}
              >
                {loading ? (
                  <Loader />
                ) : chartData.length === 0 ? (
                  <p className="text-center text-muted">Sem dados disponíveis.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={chartData}>
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis />
                      <Tooltip formatter={(value) => `$${Number(value).toFixed(2)}`} />
                      <Bar dataKey="revenue" fill="#00C49F" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardBody>
            </Card>
          </Col>

          <Col xl="6">
            <Card className="shadow" style={{ minHeight: 320 }}>
              <CardHeader>
                <h3 className="mb-0">📈 Impressões por {filterType}</h3>
              </CardHeader>

              <CardBody
                className={`${loading
                  ? "d-flex justify-content-center align-items-center"
                  : "d-flex flex-row"
                  }`}
                style={{ height: 250 }}
              >
                {loading ? (
                  <Loader />
                ) : pieData.length === 0 ? (
                  <p className="text-center text-muted">Sem dados disponíveis.</p>
                ) : (
                  <>
                    {/* 🎯 Gráfico de Pizza */}
                    <ResponsiveContainer width="60%" height={250}>
                      <PieChart>
                        <Pie
                          data={pieData}
                          dataKey="impressions"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                        >
                          {pieData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={COLORS[index % COLORS.length]}
                            />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>

                    {/* 📋 Lista ao lado */}
                    <div
                      style={{
                        width: "40%",
                        overflowY: "auto",
                        maxHeight: 250,
                      }}
                    >
                      <Table borderless size="sm">
                        <tbody>
                          {pieData
                            .sort((a, b) => b.impressions - a.impressions)
                            .map((entry, index) => (
                              <tr key={index}>
                                <td>
                                  <span
                                    style={{
                                      display: "inline-block",
                                      width: 10,
                                      height: 10,
                                      backgroundColor:
                                        COLORS[index % COLORS.length],
                                      marginRight: 8,
                                      borderRadius: "50%",
                                    }}
                                  ></span>
                                  {entry.name.length > 30
                                    ? `${entry.name.slice(0, 30)}...`
                                    : entry.name}
                                </td>
                                <td className="text-right">{entry.impressions}</td>
                              </tr>
                            ))}
                        </tbody>
                      </Table>
                    </div>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* 📋 Tabela */}
        <Row className="mt-4">
          <Col xl="12">
            <Card className="shadow">
              <CardHeader className="border-0">
                <h3 className="mb-0">📋 Detalhamento Key-Values ({filterType})</h3>
              </CardHeader>

              {loading ? (
                <Loader />
              ) : (
                <>
                  <Table className="align-items-center table-flush" responsive>
                    <thead className="thead-light">
                      <tr>
                        {[
                          { key: "date", label: "🗓 Data" },
                          { key: "value", label: "🔖 Valor UTM" },
                          { key: "unfilledImpressions", label: "⚠️ Unfilled" },
                          { key: "impressions", label: "👁️ Impressões" },
                          { key: "clicks", label: "🖱️ Cliques" },
                          { key: "revenue", label: "💰 Receita (USD)" },
                          { key: "eCPM", label: "📈 eCPM (USD)" },
                        ].map((col) => (
                          <th
                            key={col.key}
                            style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                            onClick={() => handleSort(col.key)}
                          >
                            {col.label}{" "}
                            {sortColumn === col.key ? (sortDirection === "asc" ? "▲" : "▼") : ""}
                          </th>
                        ))}
                      </tr>
                    </thead>

                    <tbody>
                      {paginatedData.map((row, idx) => (
                        <tr key={idx}>
                          <td>{formatDate(row.date)}</td>
                          <td>{row.value}</td>
                          <td>{row.unfilledImpressions}</td>
                          <td>{row.impressions}</td>
                          <td>{row.clicks}</td>
                          <td>${Number(row.revenue).toFixed(2)}</td>
                          <td>${Number(row.eCPM).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  {/* 📄 Paginação */}
                  <CardFooter className="py-4">
                    <Pagination className="pagination justify-content-end mb-0">
                      <PaginationItem disabled={currentPage === 1}>
                        <PaginationLink
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            if (currentPage > 1) setCurrentPage(currentPage - 1);
                          }}
                        >
                          <i className="fas fa-angle-left" />
                        </PaginationLink>
                      </PaginationItem>

                      {Array.from({ length: totalPages }, (_, i) => (
                        <PaginationItem key={i} active={currentPage === i + 1}>
                          <PaginationLink
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              setCurrentPage(i + 1);
                            }}
                          >
                            {i + 1}
                          </PaginationLink>
                        </PaginationItem>
                      ))}

                      <PaginationItem disabled={currentPage === totalPages}>
                        <PaginationLink
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            if (currentPage < totalPages) setCurrentPage(currentPage + 1);
                          }}
                        >
                          <i className="fas fa-angle-right" />
                        </PaginationLink>
                      </PaginationItem>
                    </Pagination>
                  </CardFooter>
                </>
              )}
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default AdManagerKeyValueReport;